import {
  HANDS_FREE_STATUSES,
  normalizeSpokenText,
  SPOKEN_PICK_STATUSES,
  type HandsFreeStatus,
  type SpokenPickExtractionInput,
  type SpokenPickOption,
  type SpokenPickStatus,
} from '@/shared-responder-v2-core';

import { toCompositeKey } from './shared-responder-v2-handsfree.helpers';
import { SPOKEN_PICK_QUESTION_TYPES } from './shared-responder-v2-spoken-pick.consts';

const SPOKEN_PICK_LIVE_STATUSES: ReadonlySet<HandsFreeStatus> = new Set([
  HANDS_FREE_STATUSES.Pending,
  HANDS_FREE_STATUSES.Listening,
  HANDS_FREE_STATUSES.Capturing,
  HANDS_FREE_STATUSES.Captured,
]);

export function toSpokenPickInputKey(
  questionId: string,
  spokenText: string
): string | null {
  const normalizedText = normalizeSpokenText(spokenText);

  return normalizedText === ''
    ? null
    : toCompositeKey([questionId, normalizedText]);
}

export function isConclusiveExtraction(
  extracted: readonly string[] | null
): extracted is readonly string[] {
  return extracted !== null && extracted.length > 0;
}

export function resolveSpokenPickStatus(
  requestKey: string | null,
  settledKey: string | null
): SpokenPickStatus {
  if (requestKey === null) {
    return SPOKEN_PICK_STATUSES.Idle;
  }

  return requestKey === settledKey
    ? SPOKEN_PICK_STATUSES.Settled
    : SPOKEN_PICK_STATUSES.Pending;
}

export function isSpokenPickLive(status: HandsFreeStatus): boolean {
  return SPOKEN_PICK_LIVE_STATUSES.has(status);
}

export interface SpokenPickQueryInput {
  readonly options: SpokenPickOption[];
  readonly questionType: (typeof SPOKEN_PICK_QUESTION_TYPES)[keyof typeof SPOKEN_PICK_QUESTION_TYPES];
  readonly transcript: string;
}

export function toSpokenPickQueryInput(
  input: SpokenPickExtractionInput
): SpokenPickQueryInput {
  return {
    options: input.options.map(({ label, value }) => ({ label, value })),
    questionType: SPOKEN_PICK_QUESTION_TYPES[input.shape],
    transcript: input.transcript.trim(),
  };
}

export function settleWithin<TValue>(
  promise: Promise<TValue | null>,
  timeoutMs: number
): Promise<TValue | null> {
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(null), timeoutMs);

    const settle = (value: TValue | null): void => {
      clearTimeout(timer);
      resolve(value);
    };

    promise.then(settle, () => settle(null));
  });
}
